import {
  Box,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TableContainer,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Divider,
  IconButton,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { useState, useEffect } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import CloseIcon from "@mui/icons-material/Close";
import { Loading } from "../../../components/Loading";
import { financeApi } from "../apiService";
import { usePlanStatus } from "../../../hooks/usePlanStatus";
import {
  commonTypographyProps,
  commonTextFieldProps,
  commonButtonProps,
  smallButtonProp,
} from "../../../utils/styles";
import { formatCurrency, formatInputCurrency } from "../../../utils/utils";
import { useToast } from "../../../components/Toast";
import { IncomeTransactionTable } from "../IncomeTransaction";
import { PAYMENT_MODES } from "./PaymentModal";

interface IncomePaymentModalProps {
  open: boolean;
  onClose: () => void;
  selectedParty: any;
  onTransactionAdded: () => void;
  summaryData?: any;
  partyType?: string;
  showTransactions?: boolean;
}

const transactionTypes = {
  Received: "Received",
  Paid: "Paid",
};

// Form validation schema
const validationSchema = Yup.object({
  transactionType: Yup.string().required("Transaction type is required"),
  paymentMode: Yup.string().required("Payment mode is required"),
  date: Yup.string().required("Date is required"),
  amount: Yup.string()
    .required("Amount is required")
    .test("is-valid-amount", "Please enter a valid amount", (value) => {
      if (!value) return false;
      const numValue = parseFloat(value.replace(/,/g, ""));
      return !isNaN(numValue) && numValue > 0;
    }),
  description: Yup.string().required("Description is required"),
});

// Initial form values
const initialValues = {
  description: "",
  amount: "",
  transactionType: transactionTypes.Received,
  date: new Date().toISOString().split("T")[0],
  paymentMode: "CASH",
};

const IncomePaymentModal: React.FC<IncomePaymentModalProps> = ({
  open,
  onClose,
  selectedParty,
  onTransactionAdded,
  summaryData,
  partyType,
  showTransactions = true,
}) => {
  const { planStatus } = usePlanStatus();
  const { showToast } = useToast();
  const [transactions, setTransactions] = useState<any[]>([]);
  const [transactionLoading, setTransactionLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [summary, setSummary] = useState<any>(
    summaryData || {
      totalDebit: 0,
      totalCredit: 0,
      totalBalance: 0,
      totalCount: 0,
    }
  );
  const [useBalanceAmount, setUseBalanceAmount] = useState(false);

  // Fetch latest 10 income transactions and summary when modal opens
  useEffect(() => {
    const initialize = async () => {
      if (!open || !selectedParty || !partyType || !showTransactions) {
        setTransactions([]);
        return;
      }
      setCurrentPage(1);
      setTransactions([]);
      setHasMoreData(true);
      await fetchTransactions(selectedParty.id, partyType, 1, false);
    };
    initialize();
  }, [open, selectedParty, partyType, showTransactions]);

  const fetchTransactions = async (
    partyId: string,
    type: string,
    page: number,
    loadMore: boolean = false
  ) => {
    if (!planStatus?.company?.id) return;
    if (loadMore) {
      setLoadingMore(true);
    } else {
      setTransactionLoading(true);
    }
    try {
      const response = await financeApi.getTransactionsByPartyId(
        type,
        partyId,
        "income",
        page,
        10
      );

      const newTransactions = response.transactions || [];
      const totalPages = response.pagination?.totalPages || 1;
      if (loadMore) {
        setTransactions((prev) => [...prev, ...newTransactions]);
      } else {
        setTransactions(newTransactions);
      }
      setCurrentPage(page);
      setHasMoreData(page < totalPages);
      if (response.summary) setSummary(response.summary);
    } catch (error) {
      console.error("Error fetching transactions:", error);
      showToast();
    } finally {
      setTransactionLoading(false);
      setLoadingMore(false);
    }
  };

  const handleCreateTransaction = async (values: any, { resetForm }: any) => {
    if (!planStatus?.company?.id || !selectedParty) return;

    try {
      const transactionData = {
        ...values,
        partyId: selectedParty.id,
        partyType: partyType,
        paymentMode: values.paymentMode,
        context: "RECEIVABLE",
        description: values.description,
        contextId: selectedParty.id,
        createdBy: planStatus?.user?.id,
        amount: parseFloat(values.amount.replace(/,/g, "")),
      };

      await financeApi.createTransaction(transactionData);

      // Reset form
      resetForm();

      // Refresh latest transactions and notify parent component
      setCurrentPage(1);
      setTransactions([]);
      setHasMoreData(true);
      await fetchTransactions(selectedParty.id, partyType as string, 1, false);
      onTransactionAdded();

      showToast("Transaction created successfully", "success");
    } catch (error) {
      console.error("Error creating transaction:", error);
      showToast();
    }
  };

  const handleClose = () => {
    onClose();
  };

  if (!selectedParty) return null;

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{ minHeight: "500px" }}
      >
        <DialogTitle sx={{ px: 1, py: 0.5 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" component="div">
              {selectedParty?.name}
            </Typography>
            <IconButton
              aria-label="close"
              onClick={handleClose}
              sx={{ color: "grey.500" }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0 }}>
          <Box sx={{ p: 2 }}>
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleCreateTransaction}
            >
              {({ values, errors, touched, setFieldValue, resetForm }) => {
                // Handle useBalanceAmount effect
                // useEffect(() => {
                //   if (useBalanceAmount && summary?.totalBalance) {
                //     setFieldValue("transactionType", transactionTypes.Received);
                //     setFieldValue("amount", Math.abs(summary.totalBalance).toString());
                //   }
                // }, [useBalanceAmount, summary?.totalBalance, setFieldValue]);

                return (
                  <Form>
                    {/* Party Summary */}
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={6} sm={2}>
                        <Typography variant="h6" sx={commonTypographyProps}>
                          Booking Amount
                        </Typography>
                        <Typography variant="body1" sx={commonTypographyProps}>
                          {formatCurrency(summary?.totalBooking || 0)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={2}>
                        <Typography variant="h6" sx={commonTypographyProps}>
                          Receivable
                        </Typography>
                        <Typography variant="body1" sx={commonTypographyProps}>
                          {formatCurrency(summary?.netReceivable || 0)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={2}>
                        <Typography variant="h6" sx={commonTypographyProps}>
                          Received
                        </Typography>
                        <Typography variant="body1" sx={commonTypographyProps}>
                          {formatCurrency(summary?.netReceived || 0)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={2}>
                        <Typography variant="h6" sx={commonTypographyProps}>
                          Balance
                        </Typography>
                        <Typography variant="body1" sx={commonTypographyProps}>
                          {formatCurrency(summary?.totalBalance)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={useBalanceAmount}
                              onChange={(e) => {
                                setUseBalanceAmount(e.target.checked);
                                if (e.target.checked) {
                                  if (summary?.totalBalance > 0) {
                                    setFieldValue(
                                      "transactionType",
                                      transactionTypes.Received
                                    );
                                  } else {
                                    setFieldValue(
                                      "transactionType",
                                      transactionTypes.Paid
                                    );
                                  }
                                  setFieldValue(
                                    "amount",
                                    Math.abs(summary.totalBalance).toString()
                                  );
                                } else {
                                  setFieldValue("amount", "");
                                }
                              }}
                              size="small"
                              sx={{
                                "&.Mui-checked": {
                                  color: "primary.main",
                                },
                              }}
                            />
                          }
                          label={
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: "0.7rem",
                                color: "text.secondary",
                              }}
                            >
                              Mark All Settled
                            </Typography>
                          }
                          sx={{
                            ml: 0,
                            "& .MuiFormControlLabel-label": {
                              fontSize: "0.7rem",
                            },
                          }}
                        />
                      </Grid>
                    </Grid>

                    <Divider sx={{ mb: 2 }} />

                    {/* Add Transaction Form */}
                    <Typography
                      variant="subtitle1"
                      sx={{ mb: 2, fontSize: "0.8rem" }}
                    >
                      Add Payment
                    </Typography>
                    <Grid container spacing={1.5}>
                      <Grid item xs={12} sm={2}>
                        <FormControl
                          required
                          fullWidth
                          error={
                            touched.transactionType &&
                            Boolean(errors.transactionType)
                          }
                        >
                          <InputLabel sx={{ fontSize: "0.75rem" }}>
                            Type
                          </InputLabel>
                          <Field
                            as={Select}
                            name="transactionType"
                            label="Type"
                            {...commonTextFieldProps.SelectProps}
                            required
                          >
                            {Object.entries(transactionTypes).map(
                              ([key, value]) => (
                                <MenuItem
                                  key={key}
                                  value={key}
                                  sx={{ fontSize: "0.75rem" }}
                                >
                                  {value}
                                </MenuItem>
                              )
                            )}
                          </Field>
                        </FormControl>
                        {touched.transactionType && errors.transactionType && (
                          <Typography
                            color="error"
                            variant="caption"
                            sx={{ fontSize: "0.65rem" }}
                          >
                            {errors.transactionType}
                          </Typography>
                        )}
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        <FormControl
                          required
                          fullWidth
                          error={
                            touched.paymentMode && Boolean(errors.paymentMode)
                          }
                        >
                          <InputLabel sx={{ fontSize: "0.75rem" }}>
                            Payment Mode
                          </InputLabel>
                          <Field
                            as={Select}
                            name="paymentMode"
                            label="Payment Mode"
                            {...commonTextFieldProps.SelectProps}
                            required
                          >
                            {Object.entries(PAYMENT_MODES).map(
                              ([key, value]) => (
                                <MenuItem
                                  key={key}
                                  value={key}
                                  sx={{ fontSize: "0.75rem" }}
                                >
                                  {value}
                                </MenuItem>
                              )
                            )}
                          </Field>
                        </FormControl>
                        {touched.paymentMode && errors.paymentMode && (
                          <Typography
                            color="error"
                            variant="caption"
                            sx={{ fontSize: "0.65rem" }}
                          >
                            {errors.paymentMode}
                          </Typography>
                        )}
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        <Field
                          as={TextField}
                          name="date"
                          required
                          fullWidth
                          label="Date"
                          type="date"
                          InputLabelProps={{ shrink: true }}
                          error={touched.date && Boolean(errors.date)}
                          helperText={touched.date && errors.date}
                          {...commonTextFieldProps}
                        />
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        <Field
                          as={TextField}
                          name="amount"
                          required
                          fullWidth
                          label="Amount"
                          type="text"
                          value={
                            values.amount
                              ? formatInputCurrency(values.amount)
                              : ""
                          }
                          onChange={(e: any) => {
                            const value = e.target.value.replace(/,/g, "");
                            if (value === "" || /^\d*\.?\d*$/.test(value)) {
                              setFieldValue("amount", value);
                            }
                          }}
                          InputProps={{
                            startAdornment: (
                              <span
                                style={{ marginRight: 8, fontSize: "0.75rem" }}
                              >
                                ₹
                              </span>
                            ),
                            inputProps: {
                              style: { textAlign: "left" },
                              maxLength: 12,
                              inputMode: "decimal",
                            },
                          }}
                          error={touched.amount && Boolean(errors.amount)}
                          helperText={touched.amount && errors.amount}
                          {...commonTextFieldProps}
                        />
                      </Grid>
                      <Grid item xs={12} sm={12}>
                        <Field
                          as={TextField}
                          name="description"
                          fullWidth
                          required
                          multiline
                          rows={1}
                          label="Description"
                          placeholder="Enter description"
                          error={
                            touched.description && Boolean(errors.description)
                          }
                          helperText={touched.description && errors.description}
                          {...commonTextFieldProps}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            justifyContent: "flex-end",
                          }}
                        >
                          <Button
                            type="button"
                            variant="outlined"
                            onClick={() => {
                              resetForm();
                              onClose();
                            }}
                            color="error"
                            {...commonButtonProps}
                          >
                            Cancel
                          </Button>
                          <Button
                            type="submit"
                            variant="contained"
                            {...commonButtonProps}
                          >
                            Add Payment
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  </Form>
                );
              }}
            </Formik>

            {/* Transactions Table with Load More */}
            {showTransactions && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography
                  variant="subtitle1"
                  sx={{ mb: 1, fontSize: "0.8rem" }}
                >
                  Transactions
                </Typography>
                {transactionLoading ? (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", py: 3 }}
                  >
                    <Loading message="Loading transactions..." />
                  </Box>
                ) : (
                  <TableContainer
                    component={Paper}
                    sx={{ mb: 2, overflowY: "auto" }}
                    onScroll={(e) => {
                      const { scrollTop, scrollHeight, clientHeight } =
                        e.currentTarget;
                      if (scrollHeight - scrollTop <= clientHeight + 100) {
                        if (selectedParty && hasMoreData && !loadingMore) {
                          const nextPage = currentPage + 1;
                          fetchTransactions(
                            selectedParty.id,
                            partyType as string,
                            nextPage,
                            true
                          );
                        }
                      }
                    }}
                  >
                    <IncomeTransactionTable transactions={transactions} />
                  </TableContainer>
                )}

                {loadingMore && (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", py: 2 }}
                  >
                    <Loading message="Loading more transactions..." />
                  </Box>
                )}

                {!hasMoreData && transactions?.length > 0 && (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", py: 1 }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      No more transactions to load
                    </Typography>
                  </Box>
                )}

                {hasMoreData && !loadingMore && transactions?.length > 0 && (
                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <Button
                      variant="outlined"
                      sx={smallButtonProp.sx}
                      size="small"
                      onClick={() => {
                        const nextPage = currentPage + 1;
                        fetchTransactions(
                          selectedParty.id,
                          partyType as string,
                          nextPage,
                          true
                        );
                      }}
                      disabled={loadingMore}
                    >
                      Load More
                    </Button>
                  </Box>
                )}
              </>
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default IncomePaymentModal;
